import React, { Suspense, useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Content from 'components/styled/Content/Content';
import Layout from 'components/styled/Layout/Layout';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import ScrollTop from 'components/utility/ScrollTop/ScrollTop';
import EditableMessage from 'components/base/EditableMessage/EditableMessage';
import getFiltersFromUrl from 'helpers/getFiltersFromUrl';

import BrowseEntitiesSkeleton from 'components/data-containers/BrowseEntities/SkeletonLoader';
import DashboardSkeleton from 'components/data-containers/Dashboard/SkeletonLoader';
import NotificationSettingsModal from '../../components/notifications/Settings';
import { useSelector, useDispatch } from 'react-redux';
import { checkIsExternalOneLabelUser } from '../../redux/reducers/authentication';
import { shouldLoadLists, loadMyLists } from 'redux/reducers/myLists';
import { EntityTypes, SalesTypes } from 'context/entityContext';
import HeaderPanel from 'components/Header/HeaderPanel/HeaderPanel';
import HeaderScrolledTopicFilters from 'components/Header/HeaderScrolledTopicFilters/HeaderScrolledTopicFilters';
import { ContentWrapper } from './styles';
import Message from 'components/base/Message/Message';
import supportsOneOf from 'helpers/supportsOneOf';
import { H2 } from 'components/base/EditableMessage/EditableMessage.style';

const BrowseOpus = React.lazy(
  (): any => import('components/data-containers/BrowseEntities/BrowseOpus')
);

const Dashboard = React.lazy(
  (): any => import('components/data-containers/Dashboard')
);

const Home: React.FC<any> = (props: any) => {
  const { location } = props;
  const [isTopicFilterInView, setIsTopicFilterInView] =
    useState<boolean>(false);
  const [selectedSalesType, setSelectedSalesType] = useState<SalesTypes>();
  const [selectedEntity, setSelectedEntity] = useState<EntityTypes>();
  const isExternalUser = useSelector(checkIsExternalOneLabelUser);
  const dispatch = useDispatch();
  useEffect(() => {
    if (dispatch(shouldLoadLists())) {
      dispatch(loadMyLists());
    }
  }, [dispatch]);

  const filtersFromUrl = useMemo(() => getFiltersFromUrl(location), [location]);
  const dashboardTitle = 'My Dashboard';

  return (
    <>
      <Helmet defer={false} title="Home" />
      <ScrollTop />

      <NotificationSettingsModal />

      <HeaderPanel
        isTopicFilterInView={isTopicFilterInView}
        entityTitle="Home"
        entityType=""
        isExternalUser={isExternalUser}
      />

      <Content>
        {/* Note: Do not remove the next <div>. It avoids flexbox to double margins between generic components. */}
        <ContentWrapper>
          {!supportsOneOf() && (
            <Layout.MaxWidth>
              <Message data-pw="MessageBlock">
                <div>
                  <H2 as="h2" bottomMargin="0.8rem">
                    Internet Explorer browser is no longer supported!
                  </H2>

                  <p>
                    Older browsers can cause your experience to be slow and
                    error-prone. <br />
                    Please upgrade or visit OPUS on another browser.
                  </p>
                </div>
              </Message>
            </Layout.MaxWidth>
          )}

          <Layout.MaxWidth>
            <EditableMessage messageId="homepage" />
          </Layout.MaxWidth>

          <Suspense fallback={<DashboardSkeleton title={dashboardTitle} />}>
            <Dashboard title={dashboardTitle} isExternalUser={isExternalUser} />
          </Suspense>

          <HeaderScrolledTopicFilters
            setIsTopicFilterInView={setIsTopicFilterInView}
            topic="home"
            selectedSalesType={selectedSalesType}
            isTimePeriodDisabled={
              selectedEntity === 'artist' ||
              selectedEntity === 'project' ||
              selectedEntity === 'label'
            }
          />

          <Suspense
            fallback={
              <BrowseEntitiesSkeleton
                title="Browse Opus"
                browseEntityType="track"
                showToggle
                enableSearch
                isBrowseTable
              />
            }
          >
            <BrowseOpus
              id="browse-opus"
              globalFilters={filtersFromUrl}
              setSelectedSalesType={setSelectedSalesType}
              setSelectedEntity={setSelectedEntity}
              isTimePeriodDisabled={
                selectedEntity === 'artist' ||
                selectedEntity === 'project' ||
                selectedEntity === 'label'
              }
            />
          </Suspense>
        </ContentWrapper>
      </Content>
      <BackToTop bottomMargin="32px" />
      <Footer />
    </>
  );
};

export default Home;
